import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { ApplicationContext } from "../models/application-context.model";
import { ApplicationContextService } from "./application-context.service";
import { AuthenticationService, UserCompany } from "./auth.service";
import {
  findPrimaryUserCompany,
  getUserCompanyId,
} from "../utils/organization-services-license.util";
import { shouldSkipPreferredCompanyAutoSelect } from "../utils/clinic-launch-context.util";

@Injectable({ providedIn: "root" })
export class ClinicCompanyAutoSelectService {
  constructor(
    private readonly _authService: AuthenticationService,
    private readonly _appContextService: ApplicationContextService,
    private readonly _router: Router
  ) {}

  /**
   * When the user has a primary organization, switch to it and enter the app
   * without showing the company picker.
   */
  tryAutoSelectPrimaryCompany(
    context: ApplicationContext,
    options?: { navigateTo?: string }
  ): Observable<boolean> {
    if (shouldSkipPreferredCompanyAutoSelect()) {
      return of(false);
    }

    const primary = findPrimaryUserCompany(context.userCompanies);
    if (!primary) {
      return of(false);
    }

    return this.attemptSwitchToCompany(context, primary, options);
  }

  /** @deprecated Use tryAutoSelectPrimaryCompany — only primary org auto-select is supported. */
  tryAutoSelectPreferredCompany(
    context: ApplicationContext,
    options?: { navigateTo?: string }
  ): Observable<boolean> {
    return this.tryAutoSelectPrimaryCompany(context, options);
  }

  switchToCompany(userCompany: UserCompany): Observable<boolean> {
    const companyId = getUserCompanyId(userCompany);
    if (!companyId) {
      return of(false);
    }
    return this.switchToCompanyIds(userCompany.userId, companyId);
  }

  switchToCompanyIds(userId: string, companyId: string): Observable<boolean> {
    return this._authService.getAuthTokenForCompany(userId, companyId).pipe(
      tap((response) => {
        const accessToken = (response?.accessToken || "").trim();
        if (!accessToken) {
          throw new Error("Identity session required to select a company");
        }
        this._authService.persistAuthData({
          accessToken,
          refreshToken: (response?.refreshToken || "").trim(),
        });
        this._appContextService.resetContext();
      }),
      map(() => true),
      catchError(() => of(false))
    );
  }

  private attemptSwitchToCompany(
    context: ApplicationContext,
    userCompany: UserCompany,
    options?: { navigateTo?: string }
  ): Observable<boolean> {
    const currentCompanyId =
      context.currentlyLoggedInUser?.companyId?.toLowerCase();
    const targetCompanyId = getUserCompanyId(userCompany)?.toLowerCase();

    if (
      currentCompanyId &&
      targetCompanyId &&
      currentCompanyId === targetCompanyId
    ) {
      if (options?.navigateTo) {
        void this._router.navigateByUrl(options.navigateTo);
      }
      return of(true);
    }

    return this.switchToCompany(userCompany).pipe(
      tap((switched) => {
        if (switched && options?.navigateTo) {
          void this._router.navigateByUrl(options.navigateTo);
        }
      }),
      catchError(() => of(false))
    );
  }
}
